// Serviço para gerenciar Web Push Notifications

const VAPID_PUBLIC_KEY = 'BBR-jd85baMX9TzCqssuE1aYsG6TXLo99WCU7iogckPSP-XOXqseY0VWvmgOZqDi6enNvKMF7Uc6agUgzlNXTCA';

// Converter chave VAPID base64 para Uint8Array
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/\-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }

  return outputArray;
}

// Verificar se o navegador suporta notificações
export function isNotificationSupported(): boolean {
  const hasRequiredAPIs = 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;

  if (!hasRequiredAPIs) {
    return false;
  }

  // Detectar se é mobile
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

  // No iOS Safari, Web Push só funciona em iOS 16.4+
  if (isMobile && /iPhone|iPad|iPod/i.test(navigator.userAgent)) {
    const match = navigator.userAgent.match(/OS (\d+)_/);
    if (match && parseInt(match[1]) < 16) {
      console.warn('[Notifications] iOS menor que 16.4 não suporta Web Push');
      return false;
    }
  }

  return true;
}

// Verificar se já tem permissão de notificação
export function hasNotificationPermission(): boolean {
  return isNotificationSupported() && Notification.permission === 'granted';
}

// Solicitar permissão de notificação
export async function requestNotificationPermission(): Promise<boolean> {
  if (!isNotificationSupported()) {
    throw new Error('Navegador não suporta notificações');
  }

  if (Notification.permission === 'granted') {
    return true;
  }

  if (Notification.permission === 'denied') {
    throw new Error('Permissão de notificação negada. Habilite nas configurações do navegador.');
  }

  const permission = await Notification.requestPermission();
  return permission === 'granted';
}

// Registrar Service Worker
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration> {
  if (!('serviceWorker' in navigator)) {
    throw new Error('Service Worker não suportado');
  }

  try {
    const registration = await navigator.serviceWorker.register('/service-worker.js');
    console.log('[Notifications] Service Worker registrado:', registration);

    // Aguardar o service worker estar ativo
    if (registration.installing) {
      await new Promise<void>((resolve) => {
        registration.installing!.addEventListener('statechange', (e) => {
          if ((e.target as ServiceWorker).state === 'activated') {
            resolve();
          }
        });
      });
    }

    return registration;
  } catch (error) {
    console.error('[Notifications] Erro ao registrar Service Worker:', error);
    throw error;
  }
}

// Obter subscription existente
export async function getSubscription(): Promise<PushSubscription | null> {
  if (!('serviceWorker' in navigator)) {
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    return await registration.pushManager.getSubscription();
  } catch (error) {
    console.error('[Notifications] Erro ao obter subscription:', error);
    return null;
  }
}

// Criar nova subscription
export async function subscribeUser(): Promise<PushSubscription> {
  try {
    const registration = await navigator.serviceWorker.ready;
    console.log('[Notifications] Service Worker pronto:', registration);

    // Verificar se já existe uma subscription
    const existingSubscription = await registration.pushManager.getSubscription();
    if (existingSubscription) {
      console.log('[Notifications] Subscription existente encontrada, cancelando...');
      await existingSubscription.unsubscribe();
    }

    // Converter VAPID key
    const applicationServerKey = urlBase64ToUint8Array(VAPID_PUBLIC_KEY);
    console.log('[Notifications] VAPID key convertida, tamanho:', applicationServerKey.length);

    // Criar nova subscription
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey,
    });

    console.log('[Notifications] Subscription criada com sucesso:', subscription);
    return subscription;
  } catch (error: any) {
    console.error('[Notifications] Erro ao criar subscription:', error);
    console.error('[Notifications] Error name:', error.name);
    console.error('[Notifications] Error message:', error.message);

    // Traduzir erros comuns
    if (error.name === 'NotAllowedError') {
      throw new Error('Permissão de notificação negada. Habilite nas configurações do navegador.');
    }

    if (error.name === 'AbortError' || error.message.includes('push service')) {
      throw new Error('Erro ao conectar com o serviço de push. Verifique sua conexão com a internet.');
    }

    throw new Error(error.message || 'Erro ao criar subscription de push');
  }
}

// Verificar se está em mobile
export function isMobileDevice(): boolean {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

// Verificar conectividade
export function isOnline(): boolean {
  return navigator.onLine;
}

// Cancelar subscription
export async function unsubscribeUser(): Promise<boolean> {
  const subscription = await getSubscription();

  if (!subscription) {
    return false;
  }

  const success = await subscription.unsubscribe();
  console.log('[Notifications] Subscription cancelada:', success);
  return success;
}

// Testar notificação local (sem backend)
export async function testLocalNotification(): Promise<void> {
  if (!hasNotificationPermission()) {
    throw new Error('Sem permissão de notificação');
  }

  const registration = await navigator.serviceWorker.ready;

  await registration.showNotification('🔔 Teste de Notificação', {
    body: 'As notificações estão funcionando!',
    icon: '/logo192.png',
    badge: '/logo192.png',
    vibrate: [200, 100, 200],
    tag: 'test',
  });
}
